import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { relaysApi } from '../api/client'
import {
  Server,
  Plus,
  Trash2,
  Edit,
  Star,
  Globe,
  Lock,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react'

interface Relay {
  id: string
  name: string
  address: string
  region: string | null
  is_default: boolean
  is_public: boolean
  status: string
  status_message: string | null
  last_health_check: string | null
  tunnel_count: number
  created_at: string
  updated_at: string
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'active':
      return (
        <span className="flex items-center gap-1 text-green-400 text-sm">
          <CheckCircle className="w-4 h-4" />
          Active
        </span>
      )
    case 'inactive':
      return (
        <span className="flex items-center gap-1 text-slate-400 text-sm">
          <XCircle className="w-4 h-4" />
          Inactive
        </span>
      )
    case 'error':
      return (
        <span className="flex items-center gap-1 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4" />
          Error
        </span>
      )
    default:
      return (
        <span className="flex items-center gap-1 text-slate-400 text-sm">
          {status}
        </span>
      )
  }
}

export default function Relays() {
  const queryClient = useQueryClient()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editRelay, setEditRelay] = useState<Relay | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['relays'],
    queryFn: () => relaysApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: relaysApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relays'] })
      setIsCreateModalOpen(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof relaysApi.update>[1] }) =>
      relaysApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relays'] })
      setEditRelay(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: relaysApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relays'] })
      setDeleteConfirm(null)
    },
  })

  const setDefaultMutation = useMutation({
    mutationFn: relaysApi.setDefault,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relays'] })
    },
  })

  const relays: Relay[] = data?.relays || []

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Server className="w-8 h-8 text-primary-500" />
          <h1 className="text-2xl font-bold text-white">Relays</h1>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Relay
        </button>
      </div>

      {isLoading ? (
        <div className="text-slate-400">Loading relays...</div>
      ) : relays.length === 0 ? (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-8 text-center">
          <Server className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No relays configured</h3>
          <p className="text-slate-400 mb-4">
            Add a relay server to enable tunnel connections.
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            Add First Relay
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {relays.map((relay) => (
            <div
              key={relay.id}
              className="bg-slate-800 rounded-lg border border-slate-700 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Server className="w-6 h-6 text-slate-400" />
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-white">{relay.name}</h3>
                      {relay.is_default && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                          <Star className="w-3 h-3" />
                          Default
                        </span>
                      )}
                      {relay.is_public ? (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                          <Globe className="w-3 h-3" />
                          Public
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-500/20 text-slate-400 text-xs rounded-full">
                          <Lock className="w-3 h-3" />
                          Private
                        </span>
                      )}
                    </div>
                    <p className="text-slate-400 font-mono text-sm">{relay.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!relay.is_default && (
                    <button
                      onClick={() => setDefaultMutation.mutate(relay.id)}
                      className="p-2 text-slate-400 hover:text-yellow-400 hover:bg-slate-700 rounded-lg transition-colors"
                      title="Set as default"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setEditRelay(relay)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(relay.id)}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors"
                    title="Delete"
                    disabled={relay.tunnel_count > 0}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-6 text-sm">
                <StatusBadge status={relay.status} />
                {relay.region && (
                  <span className="text-slate-400">
                    Region: <span className="text-white">{relay.region}</span>
                  </span>
                )}
                <span className="text-slate-400">
                  Tunnels: <span className="text-white">{relay.tunnel_count}</span>
                </span>
                {relay.last_health_check && (
                  <span className="text-slate-400">
                    Last health check:{' '}
                    <span className="text-white">
                      {new Date(relay.last_health_check).toLocaleString()}
                    </span>
                  </span>
                )}
              </div>

              {relay.status_message && (
                <p className="mt-2 text-sm text-slate-400">{relay.status_message}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {isCreateModalOpen && (
        <RelayModal
          title="Add Relay"
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Edit Modal */}
      {editRelay && (
        <RelayModal
          title="Edit Relay"
          relay={editRelay}
          onClose={() => setEditRelay(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editRelay.id, data })}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-2">Delete Relay?</h3>
            <p className="text-slate-400 mb-4">
              Are you sure you want to delete this relay? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface RelayModalProps {
  title: string
  relay?: Relay
  onClose: () => void
  onSubmit: (data: {
    name: string
    address: string
    region?: string
    is_default?: boolean
    is_public?: boolean
  }) => void
  isLoading: boolean
}

function RelayModal({ title, relay, onClose, onSubmit, isLoading }: RelayModalProps) {
  const [name, setName] = useState(relay?.name || '')
  const [address, setAddress] = useState(relay?.address || '')
  const [region, setRegion] = useState(relay?.region || '')
  const [isDefault, setIsDefault] = useState(relay?.is_default || false)
  const [isPublic, setIsPublic] = useState(relay?.is_public ?? true)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      name,
      address,
      region: region || undefined,
      is_default: isDefault,
      is_public: isPublic,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="My Relay"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Address
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="relay.example.com:7000"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Region (optional)
            </label>
            <input
              type="text"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="us-east, eu-west, etc."
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm text-slate-300">Set as default</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm text-slate-300">Public relay</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name || !address}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : relay ? 'Save Changes' : 'Add Relay'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
